// Place-an-order modal. Opens from ChannelFeed's "Заказать" button.
//
// Customer fills variant (free-form: "M, blue" / "1000IU x60" / etc.),
// qty, optional notes. Live total preview in USD + RUB. Submit posts to
// /api/orders and closes.
//
// Address handling (migration 0054):
//   The shipping fields can be filled three ways via the source picker
//   at the top of the address block:
//     - "my"       → pick from the reseller's own saved book.
//                    Default-flagged address auto-selected.
//     - "customer" → pick from a saved customer's addresses. Only
//                    available when a reseller-customer is selected
//                    above and that customer has ≥1 saved address.
//                    Primary-flagged address auto-selected.
//     - "manual"   → free-form fields (the legacy path). After the
//                    order is placed successfully, we show an inline
//                    "Save this address?" prompt that POSTs to the
//                    matching book.
//
// The order POST still ships the four flat fields (recipientName,
// recipientPhone, shipAddress, shipCountry). The address book is a
// prefill layer — the snapshot pattern on orders (migration 0016) is
// what makes historical orders independent of later book edits.
//
// localStorage prefill (the pre-0054 cache at `cg_last_ship_v1`) is
// kept as a fallback for resellers who haven't filled their address
// book yet — it pre-populates the manual fields on first open.
import { useState, useEffect, useMemo } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { Btn, Inp, Modal, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

// Last-used-address localStorage cache — kept around for resellers
// who haven't built their book yet. The book takes precedence when
// it has anything in it.
const LAST_SHIP_KEY = "cg_last_ship_v1";
function loadLastShip() {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_SHIP_KEY) || "null");
    if (v && typeof v === "object") return v;
  } catch {}
  return {};
}
function saveLastShip(s) {
  try { localStorage.setItem(LAST_SHIP_KEY, JSON.stringify(s)); } catch {}
}

// Optional reorder props (added 2026-06-05):
//   pinnedResellerCustomerId — pre-locks the "Для какого клиента" picker
//   defaultVariant, defaultQty — seed the variant / qty fields
//   previousUnitPriceCents — show price-was comparison
export function OrderModal({
  post, onClose, onDone,
  pinnedResellerCustomerId,
  defaultVariant,
  defaultQty,
  previousUnitPriceCents,
}) {
  const t = useT();
  const toast = useToast();
  const last = loadLastShip();
  const [variant, setVariant] = useState(defaultVariant ?? "");
  const [qty, setQty] = useState(defaultQty ?? 1);
  const [notes, setNotes] = useState("");
  const [recipientName, setRecipientName] = useState(last.recipientName || "");
  const [recipientPhone, setRecipientPhone] = useState(last.recipientPhone || "");
  const [shipAddress, setShipAddress] = useState(last.shipAddress || "");
  const [shipCountry, setShipCountry] = useState(last.shipCountry || t("order.modal.countryDefault"));
  const [submitting, setSubmitting] = useState(false);
  // Purchase facilitation (migration 0073). true = we facilitate (fee in the
  // final price); false = reseller self-orders, no fee/final-price math.
  const [facilitated, setFacilitated] = useState(true);
  // Reseller bookkeeping price in rubles for self-purchase (migration
  // 0075). FX-suggested from the reseller's own display rate, editable.
  const [resellerRate, setResellerRate] = useState(null);
  const [rublePrice, setRublePrice] = useState("");
  const [rubleTouched, setRubleTouched] = useState(false);

  // Address book state ───────────────────────────────────────────
  // `myAddresses` / `customerAddresses` are null while loading,
  // [] after load if empty, or a populated list. The picker UI
  // shows a spinner placeholder while null and an empty-state when
  // [], so we can be deterministic about both transitions.
  const [myAddresses, setMyAddresses] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState(null);
  const [addrSource, setAddrSource] = useState("manual"); // "my" | "customer" | "manual"
  const [selectedAddrId, setSelectedAddrId] = useState(null);

  // After a successful order with `addrSource === "manual"`, the
  // modal body switches from the order form to this save prompt so
  // the reseller can opt to persist the typed address. `null` =
  // no prompt; an object = show the prompt with this data.
  //
  // savePrompt.targetCustomerId is the customer the address should
  // attach to (a real id) or null for the reseller's own book.
  const [savePrompt, setSavePrompt] = useState(null);

  // Reseller-CRM attribution (migration 0045).
  const [resellerCustomerId, setResellerCustomerId] = useState(
    pinnedResellerCustomerId || ""
  );
  const [myCustomers, setMyCustomers] = useState(null);

  useEffect(() => {
    // Fire-and-forget load of the customer list (for the attribution
    // picker) and the reseller's own address book. Both are
    // independent so we don't gate one on the other.
    api.listMyCustomers({ limit: 200 })
      .then(setMyCustomers)
      .catch(() => setMyCustomers([]));
    api.getResellerSettings()
      .then((s) => setResellerRate(s?.fxRateRubPerUsd ?? null))
      .catch(() => {});
    api.listMyAddresses()
      .then((rows) => {
        setMyAddresses(rows);
        // Auto-pick: if there are saved addresses, switch to the
        // "my" source and pre-select the default (or first). This
        // is the common case — a returning reseller doesn't want
        // to retype their address every time.
        if (rows && rows.length > 0) {
          const def = rows.find((a) => a.isDefault) || rows[0];
          setAddrSource("my");
          setSelectedAddrId(def.id);
          applyAddress(def);
        }
      })
      .catch(() => setMyAddresses([]));
  }, []);

  // Reload customer addresses whenever the customer selection
  // changes. We never auto-switch the source TO "customer" — that
  // happens only when the reseller clicks the tab — but if the
  // user is already on "customer" and we got new addresses, we
  // auto-pick the primary.
  useEffect(() => {
    if (!resellerCustomerId) {
      setCustomerAddresses(null);
      if (addrSource === "customer") {
        // The current source no longer has any addresses to show.
        // Fall back to "my" if available, else "manual".
        setAddrSource(myAddresses && myAddresses.length > 0 ? "my" : "manual");
      }
      return;
    }
    api.listCustomerAddresses(resellerCustomerId)
      .then((rows) => {
        setCustomerAddresses(rows);
        if (addrSource === "customer" && rows.length > 0) {
          const prim = rows.find((a) => a.isPrimary) || rows[0];
          setSelectedAddrId(prim.id);
          applyAddress(prim);
        }
      })
      .catch(() => setCustomerAddresses([]));
    // We intentionally don't include addrSource in the deps —
    // changing the source is handled in the click handlers below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resellerCustomerId]);

  // Copy an address row's fields into the four free-form fields.
  // Always going through these state vars keeps submit() simple
  // (no "if source === 'my' then..." branching at submit time).
  const applyAddress = (a) => {
    if (!a) return;
    setRecipientName(a.recipientName || "");
    setRecipientPhone(a.phone || "");
    setShipAddress(a.address || "");
    setShipCountry(a.country || t("order.modal.countryDefault"));
  };

  // Tab switch handlers — keep the recipient/address fields in
  // sync with what the chosen source has selected. Switching to
  // "manual" leaves whatever was there so the reseller can tweak
  // a picked address rather than retyping from scratch.
  const switchToMy = () => {
    setAddrSource("my");
    if (myAddresses && myAddresses.length > 0) {
      const def = myAddresses.find((a) => a.isDefault) || myAddresses[0];
      setSelectedAddrId(def.id);
      applyAddress(def);
    } else {
      setSelectedAddrId(null);
    }
  };
  const switchToCustomer = () => {
    setAddrSource("customer");
    if (customerAddresses && customerAddresses.length > 0) {
      const prim = customerAddresses.find((a) => a.isPrimary) || customerAddresses[0];
      setSelectedAddrId(prim.id);
      applyAddress(prim);
    } else {
      setSelectedAddrId(null);
    }
  };
  const switchToManual = () => {
    setAddrSource("manual");
    setSelectedAddrId(null);
    // Leave the four fields as-is so an edit of a picked address
    // is one-click; if the user wants a blank slate they can clear
    // them.
  };

  // Price preview. (Unchanged from the pre-0054 flow.)
  const unitUsd = post.priceCents != null ? (post.priceCents / 100) : null;
  const totalUsd = unitUsd != null ? unitUsd * qty : null;
  const unitRub = post.priceRubCents != null ? (post.priceRubCents / 100) : null;
  const totalRub = unitRub != null ? unitRub * qty : null;
  const feePct = post.channel?.organizerFeePct ?? 0;
  const feeUsd = (feePct > 0 && totalUsd != null) ? totalUsd * feePct / 100 : 0;
  const feeRub = (feePct > 0 && totalRub != null) ? totalRub * feePct / 100 : 0;
  const grandUsd = totalUsd != null ? totalUsd + feeUsd : null;
  const grandRub = totalRub != null ? totalRub + feeRub : null;
  const cashbackPct = post.channel?.cashbackPct ?? 0;
  const cashbackUsd = (cashbackPct > 0 && totalUsd != null) ? totalUsd * cashbackPct / 100 : 0;
  const cashbackRub = (cashbackPct > 0 && totalRub != null) ? totalRub * cashbackPct / 100 : 0;

  // Pretty name for the currently-selected customer — used in the
  // save-prompt body so the user knows where the address will go.
  const selectedCustomerName = useMemo(() => {
    if (!resellerCustomerId || !myCustomers) return null;
    const c = myCustomers.find((x) => x.id === resellerCustomerId);
    return c ? c.name : null;
  }, [resellerCustomerId, myCustomers]);

  const submit = async () => {
    if (!recipientName.trim()) { toast.error(t("order.recipient.required")); return; }
    if (!shipAddress.trim()) { toast.error(t("order.address.required")); return; }
    setSubmitting(true);
    try {
      // Reseller bookkeeping price on self-purchase: use the typed value,
      // or fall back to the FX suggestion if untouched.
      let resellerPriceRubCents;
      if (!facilitated) {
        const suggested = resellerRate ? Math.round((totalUsd ?? 0) * resellerRate) : null;
        const shown = rubleTouched ? rublePrice : (suggested != null ? String(suggested) : "");
        const cents = shown.trim() === "" ? null : Math.round(parseFloat(shown) * 100);
        if (cents != null && Number.isFinite(cents) && cents >= 0) resellerPriceRubCents = cents;
      }
      await api.placeOrder(post.id, {
        variant: variant.trim() || undefined,
        qty,
        notes: notes.trim() || undefined,
        recipientName: recipientName.trim(),
        recipientPhone: recipientPhone.trim() || undefined,
        shipAddress: shipAddress.trim(),
        shipCountry: shipCountry.trim() || undefined,
        resellerCustomerId: resellerCustomerId || undefined,
        facilitated,
        resellerPriceRubCents,
      });
      saveLastShip({
        recipientName: recipientName.trim(),
        recipientPhone: recipientPhone.trim(),
        shipAddress: shipAddress.trim(),
        shipCountry: shipCountry.trim(),
      });
      toast.success(t("order.placed.ok"));
      onDone?.();
      // Save-prompt only when the user typed a fresh address. Picks
      // from either book don't need re-saving.
      if (addrSource === "manual") {
        setSavePrompt({
          recipientName: recipientName.trim(),
          phone: recipientPhone.trim() || null,
          address: shipAddress.trim(),
          country: shipCountry.trim() || "RU",
          // Attach to the linked customer if one was set, else the
          // reseller's own book.
          targetCustomerId: resellerCustomerId || null,
          targetCustomerName: selectedCustomerName,
        });
        // Caller closes via onDone/onClose on Save/Skip below.
      } else {
        onClose();
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Save-prompt branch ───────────────────────────────────────
  if (savePrompt) {
    return (
      <SaveAddressPrompt
        data={savePrompt}
        onDone={() => { setSavePrompt(null); onClose(); }}
        onClose={onClose}
      />
    );
  }

  // ─── Source tab styles ────────────────────────────────────────
  const tabBtn = (active) => ({
    flex: 1, padding: "8px 10px",
    background: active ? T_NEW.accent : "transparent",
    color: active ? "#fff" : T_NEW.ink,
    border: `1px solid ${active ? T_NEW.accent : T_NEW.ink12}`,
    borderRadius: T_NEW.r1,
    cursor: "pointer",
    fontSize: 13, fontWeight: 600,
    fontFamily: T_NEW.fontSans,
  });

  // The "customer" tab is enabled only when a CRM customer is
  // selected. Showing it disabled when no customer is picked is the
  // visual cue that the customer-address path requires that prior
  // step.
  const customerTabDisabled = !resellerCustomerId;

  return (
    <Modal title={t("order.modal.title")} onClose={onClose} width={420}>
      {/* Post summary — Serif title + Mono price */}
      <div style={{ display: "flex", gap: 12, marginBottom: T_NEW.s5, background: T_NEW.paperSoft, padding: 12, borderRadius: T_NEW.r2 }}>
        {post.images?.[0]?.url && (
          <img src={post.images[0].url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: T_NEW.r1, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Serif size={T_NEW.bodyLg} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {post.title}
          </Serif>
          {unitUsd != null && (
            <Mono size={T_NEW.meta} color={T_NEW.ink56} style={{ display: "block", marginTop: 4 }}>
              ${unitUsd.toFixed(2)} · ед.
            </Mono>
          )}
          {previousUnitPriceCents != null && unitUsd != null &&
           Math.abs(previousUnitPriceCents - post.priceCents) > 10 && (
            <Mono size={T_NEW.micro} color={T_NEW.ink56}
                  style={{ display: "block", marginTop: 2 }}>
              <span style={{ textDecoration: "line-through" }}>
                {t("order.modal.priceWas", { amt: (previousUnitPriceCents / 100).toFixed(2) })}
              </span>
              {t("order.modal.priceNow")}
              <span style={{ color: previousUnitPriceCents > post.priceCents
                                       ? T_NEW.bottle
                                       : T_NEW.danger,
                             fontWeight: 600 }}>
                ${unitUsd.toFixed(2)}
              </span>
            </Mono>
          )}
        </div>
      </div>

      {/* Reseller-CRM attribution (migration 0045) */}
      {myCustomers !== null && myCustomers.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Mono style={{ display: "block", marginBottom: 6 }}>
            {t("order.modal.forCustomer")}
            {pinnedResellerCustomerId && (
              <span style={{ marginLeft: 8, fontSize: 11,
                             color: T_NEW.ink56, fontWeight: 400 }}>
                {t("order.modal.reorderTag")}
              </span>
            )}
          </Mono>
          <select
            value={resellerCustomerId}
            onChange={(e) => setResellerCustomerId(e.target.value)}
            disabled={!!pinnedResellerCustomerId}
            style={{ width: "100%", background: T.bg, color: T.text,
                     border: `1px solid ${T.border}`,
                     padding: "12px 14px", borderRadius: 12, fontSize: 15,
                     fontFamily: "inherit",
                     opacity: pinnedResellerCustomerId ? 0.7 : 1,
                     cursor: pinnedResellerCustomerId ? "not-allowed" : "pointer" }}>
            <option value="">{t("order.modal.forMe")}</option>
            {myCustomers.map((cust) => (
              <option key={cust.id} value={cust.id}>
                {cust.name}
                {cust.telegramHandle ? ` (${cust.telegramHandle})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <Inp label={t("order.variant")} value={variant} onChange={setVariant}
           placeholder={t("order.variant.placeholder")} autoFocus />

      <div style={{ marginBottom: 14 }}>
        <Mono style={{ display: "block", marginBottom: 6 }}>{t("order.qty")}</Mono>
        <input
          type="number" min={1} max={1000}
          value={qty}
          onChange={(e) => setQty(Math.max(1, Math.min(1000, parseInt(e.target.value || "1", 10))))}
          style={{ width: "100%", background: T.bg, color: T.text, border: `1px solid ${T.border}`,
                   padding: "12px 14px", borderRadius: 12, fontSize: 15 }}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: T.textSec, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>
          {t("order.notes")}
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t("order.notes.placeholder")}
          style={{ width: "100%", background: T.bg, color: T.text, border: `1px solid ${T.border}`,
                   padding: "10px 12px", borderRadius: 10, fontSize: 14, minHeight: 64,
                   resize: "vertical", fontFamily: "inherit" }}
        />
      </div>

      {/* Address block: source picker (tabs) + content per source */}
      <div style={{ background: T_NEW.paperSoft, padding: 14, borderRadius: T_NEW.r2, marginBottom: T_NEW.s4 }}>
        <Mono color={T_NEW.ink} style={{ display: "block", marginBottom: 10 }}>
          {t("order.address.source.label")}
        </Mono>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          <button type="button" style={tabBtn(addrSource === "my")}
                  onClick={switchToMy}>
            {t("order.address.source.my")}
          </button>
          <button type="button" style={{
            ...tabBtn(addrSource === "customer"),
            opacity: customerTabDisabled ? 0.4 : 1,
            cursor: customerTabDisabled ? "not-allowed" : "pointer",
          }} disabled={customerTabDisabled}
                  onClick={() => !customerTabDisabled && switchToCustomer()}>
            {t("order.address.source.customer")}
          </button>
          <button type="button" style={tabBtn(addrSource === "manual")}
                  onClick={switchToManual}>
            {t("order.address.source.manual")}
          </button>
        </div>

        {addrSource === "my" && (
          <AddressList
            rows={myAddresses}
            selectedId={selectedAddrId}
            onSelect={(a) => { setSelectedAddrId(a.id); applyAddress(a); }}
            badgeKey="isDefault"
            badgeLabel={t("order.address.picker.default")}
            emptyText={t("order.address.empty.my")}
          />
        )}

        {addrSource === "customer" && (
          resellerCustomerId ? (
            <AddressList
              rows={customerAddresses}
              selectedId={selectedAddrId}
              onSelect={(a) => { setSelectedAddrId(a.id); applyAddress(a); }}
              badgeKey="isPrimary"
              badgeLabel={t("order.address.picker.primary")}
              emptyText={t("order.address.empty.customer")}
            />
          ) : (
            <Hint text={t("order.address.empty.noCustomer")} />
          )
        )}

        {addrSource === "manual" && (
          <>
            <Inp label={t("order.recipient.name") + " *"} value={recipientName} onChange={setRecipientName}
                 placeholder={t("order.recipient.name.placeholder")} />
            <Inp label={t("order.recipient.phone")} value={recipientPhone} onChange={setRecipientPhone}
                 placeholder={t("order.modal.phonePlaceholder")} />
            <div style={{ marginBottom: 14 }}>
              <Mono style={{ display: "block", marginBottom: 6 }}>{t("order.address")} *</Mono>
              <textarea
                value={shipAddress}
                onChange={(e) => setShipAddress(e.target.value)}
                placeholder={t("order.address.placeholder")}
                style={{ width: "100%", background: T.sidebar, color: T.text, border: `1px solid ${T.border}`,
                         padding: "10px 12px", borderRadius: 10, fontSize: 14, minHeight: 64,
                         resize: "vertical", fontFamily: "inherit" }}
              />
            </div>
            <Inp label={t("order.country")} value={shipCountry} onChange={setShipCountry}
                 placeholder={t("order.modal.countryDefault")} />
          </>
        )}
      </div>

      {/* Facilitation choice (migration 0073): we buy it (fee in the price)
          vs. the reseller orders it themselves (no fee, no final-price math). */}
      <div style={{ marginBottom: T_NEW.s4 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("order.facilitate.title")}
        </Mono>
        <div style={{ display: "flex", gap: 8 }}>
          {[[true, t("order.facilitate.byUs")], [false, t("order.facilitate.self")]].map(([val, label]) => (
            <button key={String(val)} type="button" onClick={() => setFacilitated(val)}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                       border: `1px solid ${facilitated === val ? T_NEW.persimmon : T_NEW.ink12}`,
                       background: facilitated === val ? T_NEW.persimmonSoft : "transparent",
                       color: T_NEW.ink, fontSize: T_NEW.meta, fontFamily: T_NEW.fontSans }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Self-purchase bookkeeping price (₽). FX-suggested, editable. */}
      {!facilitated && (() => {
        const suggested = resellerRate ? Math.round((totalUsd ?? 0) * resellerRate) : null;
        const shown = rubleTouched ? rublePrice : (suggested != null ? String(suggested) : "");
        return (
          <div style={{ marginBottom: T_NEW.s4 }}>
            <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
              {t("order.rublePrice.title")}
            </Mono>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>₽</span>
              <input
                value={shown}
                inputMode="numeric"
                onChange={(e) => { setRubleTouched(true); setRublePrice(e.target.value); }}
                placeholder={t("cart.rublePrice")}
                style={{ flex: 1, background: T.bg, color: T.text, border: `1px solid ${T.border}`,
                         padding: "10px 12px", borderRadius: 10, fontSize: 15, fontFamily: T_NEW.fontMono }}
              />
            </div>
            {resellerRate && !rubleTouched && (
              <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 4, fontFamily: T_NEW.fontSans }}>
                {t("cart.fxSuggested", { rate: resellerRate })}
              </div>
            )}
          </div>
        );
      })()}

      {totalUsd != null && (
        <div style={{ background: T_NEW.paperSoft, padding: "12px 16px", borderRadius: T_NEW.r2, marginBottom: T_NEW.s5 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: T_NEW.meta, color: T_NEW.ink56, marginBottom: (facilitated && feePct > 0) ? 6 : 0, fontFamily: T_NEW.fontSans }}>
            <span>{t("order.subtotal")}</span>
            <span style={{ fontFamily: T_NEW.fontMono }}>${totalUsd.toFixed(2)}{totalRub != null ? ` · ₽${totalRub.toFixed(0)}` : ""}</span>
          </div>
          {facilitated && feePct > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: T_NEW.meta, color: T_NEW.ink56, marginBottom: 6, fontFamily: T_NEW.fontSans }}>
              <span>{t("order.organizerFee", { pct: feePct })}</span>
              <span style={{ fontFamily: T_NEW.fontMono }}>${feeUsd.toFixed(2)}{feeRub ? ` · ₽${feeRub.toFixed(0)}` : ""}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: (facilitated && feePct > 0) ? 10 : 0, borderTop: (facilitated && feePct > 0) ? `1px solid ${T_NEW.ink12}` : "none" }}>
            <Mono color={T_NEW.ink} style={{ fontWeight: 600 }}>{t("order.total")}</Mono>
            <span style={{ textAlign: "right" }}>
              <Serif size={T_NEW.displayMd} color={T_NEW.ink} style={{ display: "inline-block", lineHeight: 1 }}>
                ${(facilitated ? grandUsd : totalUsd).toFixed(2)}
              </Serif>
              {(facilitated ? grandRub : totalRub) != null && (
                <span style={{ display: "block", fontSize: T_NEW.meta, color: T_NEW.ink56, fontFamily: T_NEW.fontMono, marginTop: 4 }}>
                  ≈ ₽{(facilitated ? grandRub : totalRub).toFixed(0)}
                </span>
              )}
            </span>
          </div>
          {!facilitated && (
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 8, fontFamily: T_NEW.fontSans, fontStyle: "italic" }}>
              {t("order.facilitate.selfNote")}
            </div>
          )}
          {facilitated && cashbackPct > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: T_NEW.meta,
                          color: T_NEW.bottle, marginTop: 10, paddingTop: 10,
                          borderTop: `1px dashed ${T_NEW.ink12}`, fontWeight: 600,
                          fontFamily: T_NEW.fontSans }}>
              <span>{t("order.cashback.preview", { pct: cashbackPct })}</span>
              <span style={{ fontFamily: T_NEW.fontMono }}>+${cashbackUsd.toFixed(2)}{cashbackRub ? ` · ₽${cashbackRub.toFixed(0)}` : ""}</span>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>{t("common.cancel")}</Btn>
        <Btn disabled={submitting} onClick={submit}>{submitting ? t("order.placing") : t("order.place")}</Btn>
      </div>
    </Modal>
  );
}

// ─── AddressList: radio-style list of saved addresses ────────────
export function AddressList({ rows, selectedId, onSelect, badgeKey, badgeLabel, emptyText }) {
  // null = still loading (don't render either the empty-state or
  // the list); [] = loaded and empty.
  if (rows == null) {
    return <div style={{ padding: 14, textAlign: "center",
                         color: T_NEW.ink56, fontSize: 13 }}>…</div>;
  }
  if (rows.length === 0) {
    return <Hint text={emptyText} />;
  }
  return (
    <div>
      {rows.map((a) => {
        const sel = a.id === selectedId;
        return (
          <button key={a.id} type="button" onClick={() => onSelect(a)}
                  style={{ display: "block", width: "100%", textAlign: "left",
                           padding: "10px 12px", marginBottom: 6,
                           background: sel ? T_NEW.accent + "12" : T_NEW.paper,
                           border: `1.5px solid ${sel ? T_NEW.accent : T_NEW.ink12}`,
                           borderRadius: T_NEW.r1,
                           cursor: "pointer",
                           fontFamily: T_NEW.fontSans }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: T_NEW.ink,
                             overflow: "hidden", textOverflow: "ellipsis",
                             whiteSpace: "nowrap", flex: 1 }}>
                {a.label || a.recipientName}
              </span>
              {a[badgeKey] && (
                <span style={{ fontSize: 10, fontWeight: 700,
                               letterSpacing: 0.4, textTransform: "uppercase",
                               color: T_NEW.accent }}>
                  {badgeLabel}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: T_NEW.ink56, lineHeight: 1.45 }}>
              {a.recipientName}{a.phone ? ` · ${a.phone}` : ""}
            </div>
            <div style={{ fontSize: 12, color: T_NEW.ink56, lineHeight: 1.45,
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap" }}>
              {a.address}{a.country && a.country !== "RU" ? ` · ${a.country}` : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function Hint({ text }) {
  return (
    <div style={{ padding: 14, background: T_NEW.paper,
                  border: `1px dashed ${T_NEW.ink12}`,
                  borderRadius: T_NEW.r1,
                  fontSize: 12.5, color: T_NEW.ink56,
                  fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                  lineHeight: 1.5 }}>
      {text}
    </div>
  );
}

// ─── SaveAddressPrompt: shown after a manual-address order ───────
function SaveAddressPrompt({ data, onDone, onClose }) {
  const t = useT();
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        label: label.trim() || data.recipientName.slice(0, 80),
        recipientName: data.recipientName,
        phone: data.phone,
        address: data.address,
        country: data.country,
      };
      if (data.targetCustomerId) {
        await api.createCustomerAddress(data.targetCustomerId, payload);
      } else {
        await api.createMyAddress(payload);
      }
      toast.success(t("order.address.savePrompt.saved"));
    } catch (e) {
      toast.error(e.message || t("order.address.savePrompt.saveFail"));
    } finally {
      setSaving(false);
      onDone();
    }
  };

  return (
    <Modal title={t("order.address.savePrompt.title")} onClose={onClose} width={420}>
      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                    fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                    marginBottom: 16, lineHeight: 1.5 }}>
        {data.targetCustomerId
          ? t("order.address.savePrompt.bodyCustomer", { name: data.targetCustomerName || "" })
          : t("order.address.savePrompt.bodyMy")}
      </div>

      <div style={{ background: T_NEW.paperSoft, padding: 12,
                    borderRadius: T_NEW.r1, marginBottom: 14,
                    fontSize: 12.5, color: T_NEW.ink, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>{data.recipientName}</div>
        {data.phone && <div style={{ color: T_NEW.ink56 }}>{data.phone}</div>}
        <div style={{ color: T_NEW.ink56 }}>
          {data.address}{data.country && data.country !== "RU" ? ` · ${data.country}` : ""}
        </div>
      </div>

      <Inp label={t("order.address.savePrompt.label")}
           value={label} onChange={setLabel}
           placeholder={t("order.address.savePrompt.labelPh")}
           autoFocus />

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onDone}>
          {t("order.address.savePrompt.skip")}
        </Btn>
        <Btn disabled={saving} onClick={save}>
          {saving ? "…" : t("order.address.savePrompt.save")}
        </Btn>
      </div>
    </Modal>
  );
}
